import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Key, Trash2, Loader2, Copy, Code, ExternalLink } from "lucide-react";
import Upgrade from '../modals/Upgrade';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useUser } from "../user/UserContext";
import { LicenseType } from '../types';
import { formatDate } from '../utils/dateFormatter';

interface ApiKey {
    id: string;
    name: string;
    key_prefix: string;
    created_at: string;
    last_used: string | null;
}

interface NewApiKeyResponse {
    id: string;
    name: string;
    key: string;
    created_at: string;
}

const ALLOWED_LICENSES = [LicenseType.BUSINESS, LicenseType.AIMDOC];

const APIKeys: React.FC = () => {
    const { get, post, remove } = useApi();
    const { organization, checkLicenseType } = useUser();
    
    // Access control
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [checkingLicense, setCheckingLicense] = useState<boolean>(true);
    const [upgradeModalVisible, setUpgradeModalVisible] = useState<boolean>(false);
    
    // State management
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
    
    // Modal states
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [keyName, setKeyName] = useState<string>('');
    const [newApiKey, setNewApiKey] = useState<NewApiKeyResponse | null>(null);
    const [showNewKeyModal, setShowNewKeyModal] = useState<boolean>(false);

    // Check license access on mount and when organization changes
    useEffect(() => {
        if (organization) {
            const access = ALLOWED_LICENSES.some(license => 
                checkLicenseType(license)
            );
            setHasAccess(access);
            setCheckingLicense(false);
            
            // Only fetch API keys if user has access
            if (access && organization.id) {
                fetchApiKeys();
            }
        }
    }, [organization, checkLicenseType]);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            const response = await get('/api-keys');
            setApiKeys(response.data || response);
        } catch (error) {
            console.error('Failed to fetch API keys:', error);
            toast.error('Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!keyName.trim()) {
            toast.error('Please enter a name for the API key');
            return;
        }

        setCreating(true);
        try {
            const response = await post('/api-keys', { name: keyName });
            setNewApiKey(response.data || response);
            setCreateModalOpen(false);
            setKeyName('');
            setShowNewKeyModal(true);
            await fetchApiKeys();
        } catch (error) {
            console.error('Failed to create API key:', error);
            toast.error('Failed to create API key');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteKey = async (keyId: string) => {
        if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
            return;
        }

        setDeletingKeyId(keyId);
        try {
            await remove(`/api-keys/${keyId}`);
            toast.success('API key revoked successfully');
            await fetchApiKeys();
        } catch (error) {
            console.error('Failed to delete API key:', error);
            toast.error('Failed to revoke API key');
        } finally {
            setDeletingKeyId(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    // Show loading while checking license
    if (checkingLicense) {
        return (
            <div className="flex justify-center items-center min-h-[350px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Show upgrade screen if no access
    if (!hasAccess) {
        return (
            <>
                {/* Upgrade Modal */}
                <Upgrade 
                    showModal={upgradeModalVisible} 
                    setShowModal={setUpgradeModalVisible}
                    sessionId={null}
                />

                {/* Centered upgrade card */}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <Card className="max-w-md bg-background">
                            <CardHeader className="text-center">
                                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                    <Key className="h-8 w-8 text-primary" />
                                </div>
                                <CardTitle className="text-xl">API Keys</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center space-y-4">
                                <p className="text-muted-foreground">
                                    Create and manage API keys for programmatic access to your Aimdoc data.
                                </p>
                                <div className="space-y-2 text-sm text-left">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <span>Secure API key generation</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                                        <span>Fetch conversations and leads</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                                        <span>Export messages and data</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                                        <span>Integrate with your systems</span>
                                    </div>
                                </div>
                                <Button 
                                    onClick={() => setUpgradeModalVisible(true)}
                                    className="w-full"
                                    size="lg"
                                >
                                    Upgrade to Convert
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    Available on the Convert plan
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Blurred background content */}
                    <div className="blur-sm pointer-events-none opacity-50">
                        <Card>
                            <CardHeader>
                                <CardTitle>API Keys</CardTitle>
                                <CardDescription>Create and manage API keys to access Aimdoc data programmatically</CardDescription>
                            </CardHeader>
                            <CardContent className="min-h-[350px]">
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="space-y-1.5">
                            <CardTitle className="flex items-center gap-2">
                                <Key className="h-5 w-5" />
                                API Keys
                            </CardTitle>
                            <CardDescription>
                                Create and manage API keys to access Aimdoc data programmatically
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => window.open('https://api.aimdoc.ai/api/v1/redoc', '_blank')}
                            >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View API Docs
                            </Button>
                            <Button onClick={() => setCreateModalOpen(true)} className="flex items-center gap-2">
                                <Plus className="h-4 w-4" />
                                Create API Key
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center items-center h-[350px]">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[350px] text-center">
                            <div className="bg-muted/50 rounded-full p-6 mb-4">
                                <Key className="h-12 w-12 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No API keys yet</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mb-6">
                                Create your first API key to start accessing Aimdoc data programmatically
                            </p>
                            <Button onClick={() => setCreateModalOpen(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Your First API Key
                            </Button>
                        </div>
                    ) : (
                        <div className="min-h-[350px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Key</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Last Used</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                {apiKeys.map((apiKey) => (
                                    <TableRow key={apiKey.id}>
                                        <TableCell className="font-medium">{apiKey.name}</TableCell>
                                        <TableCell>
                                            <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
                                                {apiKey.key_prefix}
                                            </code>
                                        </TableCell>
                                        <TableCell>{formatDate(apiKey.created_at, 'short')}</TableCell>
                                        <TableCell>
                                            {apiKey.last_used ? (
                                                formatDate(apiKey.last_used, 'short')
                                            ) : (
                                                <span className="text-muted-foreground">Never</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteKey(apiKey.id)}
                                                disabled={deletingKeyId === apiKey.id}
                                            >
                                                {deletingKeyId === apiKey.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create API Key Modal */}
            <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create API Key</DialogTitle>
                        <DialogDescription>
                            Give your API key a descriptive name to help you remember what it's used for.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="key-name">Key Name</Label>
                            <Input
                                id="key-name"
                                placeholder="e.g., Production Integration, Analytics Dashboard"
                                value={keyName}
                                onChange={(e) => setKeyName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleCreateKey()}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateKey} disabled={creating}>
                            {creating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Key'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Show New API Key Modal */}
            <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>API Key Created</DialogTitle>
                        <DialogDescription>
                            Save this key somewhere safe. You won't be able to see it again!
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <Alert>
                            <AlertDescription className="font-mono text-sm break-all">
                                {newApiKey?.key}
                            </AlertDescription>
                        </Alert>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => newApiKey && copyToClipboard(newApiKey.key)}
                        >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy to Clipboard
                        </Button>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowNewKeyModal(false)}>
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default APIKeys;

